import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/kit";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Icon } from "@/lib/icons";
import JoinWizard from "@/pages/membership/JoinWizard";
import { Txt } from "@/components/Txt";
import NeedsAttention from "@/components/NeedsAttention";
import GardenSnapshot from "@/components/GardenSnapshot";
import MyMessages from "@/components/MyMessages";
import MyLegacyBricks from "@/components/MyLegacyBricks";
import WaysToGive from "@/components/giving/WaysToGive";
import DigitalCard from "@/components/DigitalCard";
import MemberSubmit from "@/components/MemberSubmit";
import PhotoUpload from "@/components/PhotoUpload";

const PT_LABEL = { meeting: "Meeting attendance", event: "Event attendance", payment: "Membership payment", donation: "Donation", volunteer: "Volunteering", household: "Household attendance", redemption: "Reward redeemed", redemption_refund: "Redemption refunded" };
const ptDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");

export default function MemberDashboard({ me, onNavigate }) {
  const { persona } = useAuth();
  const first = (me.user.name || "there").split(" ")[0];
  const [member, setMember] = useState(undefined);
  const [points, setPoints] = useState(null);
  const [newsletter, setNewsletter] = useState("");
  const [volCount, setVolCount] = useState(0);
  const [eventsGoing, setEventsGoing] = useState(0);
  const [pinEdit, setPinEdit] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [showPin, setShowPin] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get("/membership/me");
    setMember(r.data.member);
    try { const p = await api.get("/points/me"); setPoints(p.data); } catch (e) {}
    try { const nl = await api.get("/newsletter-link"); setNewsletter(nl.data.url || ""); } catch (e) {}
    try { const v = await api.get("/events/my-volunteering"); setVolCount(v.data.total_roles || 0); } catch (e) {}
    try {
      const ev = await api.get("/events/calendar/feed", { params: { limit: 100, ...(persona ? { persona } : {}) } });
      setEventsGoing((ev.data.items || []).filter((e) => e.my_rsvp === "going").length);
    } catch (e) {}
  }, [persona]);
  useEffect(() => { load(); }, [load]);
  const go = (id) => onNavigate && onNavigate(id);

  const changePin = async () => {
    if (!/^\d{4}$/.test(newPin)) return toast.error("PIN must be 4 digits.");
    try { await api.put("/membership/me/pin", { pin: newPin }); toast.success("PIN updated"); setPinEdit(false); setNewPin(""); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update PIN"); }
  };

  if (member === undefined) return <div className="text-ink/60 py-8">Loading…</div>;

  if (member === null) {
    return (
      <div className="space-y-5">
        <div>
          <h1 className="font-zilla text-3xl font-semibold">Welcome, {first}</h1>
          <p className="text-[16px] text-ink/70">Finish setting up your Rosemoor membership.</p>
        </div>
        <JoinWizard onDone={load} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="member-home">
      <div>
        <h1 className="font-zilla text-3xl font-semibold">Good day, {first}</h1>
        <p className="text-[16px] text-ink/70">Your Rosemoor membership.</p>
      </div>

      {newsletter && (
        <a href={newsletter} target="_blank" rel="noopener noreferrer" data-testid="newsletter-cta"
           className="inline-flex items-center gap-2 text-[14px] font-semibold text-maroon hover:underline">
          <Icon name="ExternalLink" className="w-4 h-4" /> See the latest newsletter
        </a>
      )}

      <div className="grid grid-cols-3 gap-3" data-testid="member-tiles">
        <button data-testid="tile-volunteering" onClick={() => go("volunteering")} className="text-left bg-cream border border-ink/15 rounded-xl p-4 hover:border-maroon/50 transition-colors">
          <Icon name="HandHeart" className="w-5 h-5 text-maroon mb-1" />
          <div className="font-zilla text-2xl font-semibold">{volCount}</div>
          <div className="text-[12px] uppercase tracking-wide text-ink/55 font-semibold">Volunteer roles</div>
        </button>
        <button data-testid="tile-events" onClick={() => go("events")} className="text-left bg-cream border border-ink/15 rounded-xl p-4 hover:border-maroon/50 transition-colors">
          <Icon name="Calendar" className="w-5 h-5 text-maroon mb-1" />
          <div className="font-zilla text-2xl font-semibold">{eventsGoing}</div>
          <div className="text-[12px] uppercase tracking-wide text-ink/55 font-semibold">Events going</div>
        </button>
        <button data-testid="tile-points" onClick={() => go("points_rewards")} className="text-left bg-cream border border-ink/15 rounded-xl p-4 hover:border-maroon/50 transition-colors">
          <Icon name="Gift" className="w-5 h-5 text-maroon mb-1" />
          <div className="font-zilla text-2xl font-semibold">{points?.balance ?? 0}</div>
          <div className="text-[12px] uppercase tracking-wide text-ink/55 font-semibold">Points</div>
        </button>
      </div>

      <MemberSubmit />

      <WaysToGive prefill={{ name: me.user.name, email: me.user.email }} onSeeAll={() => go("give")} />

      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="my-photo">
        <h3 className="font-zilla text-lg font-semibold mb-3">Your profile photo</h3>
        <PhotoUpload endpoint="/membership/me/photo" photoUrl={member.photo_url}
          onUploaded={(url) => setMember({ ...member, photo_url: url })} testid="my-photo-upload" />
      </div>

      <DigitalCard />
      <NeedsAttention showGovernance={false} />
      <GardenSnapshot onNavigate={onNavigate} memberId={member.member_id} />
      <MyMessages />
      <MyLegacyBricks />

      {/* Points history */}
      {points !== null && (points.ledger || []).length > 0 && (
        <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="my-points-ledger">
          <h2 className="font-zilla text-xl font-semibold mb-2">Points history</h2>
          <div className="divide-y divide-ink/8">
            {points.ledger.slice(0, 6).map((e) => (
              <div key={e.pe_id} className="flex items-center justify-between py-2">
                <div><div className="text-[15px]">{PT_LABEL[e.kind] || e.kind}</div><div className="text-[12px] text-ink/50">{ptDate(e.at)}</div></div>
                <span className={`font-semibold text-[15px] ${e.points < 0 ? "text-maroon" : "text-dark-olive"}`}>{e.points > 0 ? "+" : ""}{e.points}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PIN self-service */}
      <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="my-pin">
        <h2 className="font-zilla text-xl font-semibold mb-2"><Txt id="member.dashboard.pin_heading" screen="Member dashboard">Your check-in PIN</Txt></h2>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="font-mono text-xl tracking-widest bg-warm-cream border border-ink/15 px-3 py-1 rounded" data-testid="my-pin-value">{showPin ? member.pin : "••••"}</span>
          <button data-testid="toggle-pin" onClick={() => setShowPin((s) => !s)} className="border border-ink/20 rounded-lg px-3 py-2 min-h-[40px] text-[14px] font-semibold">{showPin ? "Hide" : "Show"}</button>
          <button data-testid="change-pin" onClick={() => setPinEdit((v) => !v)} className="border border-ink/20 rounded-lg px-3 py-2 min-h-[40px] text-[14px] font-semibold"><Txt id="member.dashboard.pin_change" screen="Member dashboard">Change PIN</Txt></button>
        </div>
        {pinEdit && (
          <div className="flex gap-2 mt-3">
            <input data-testid="new-pin-input" inputMode="numeric" maxLength={4} placeholder="New 4-digit PIN" value={newPin} onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))} className="bg-warm-cream border border-ink/15 rounded-lg px-3 py-2.5 min-h-[44px] text-[15px]" />
            <Button data-testid="save-pin" onClick={changePin} className="px-4 min-h-[44px]">Save</Button>
          </div>
        )}
      </div>

      {(member.guardians || []).length > 0 && (
        <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="my-guardians">
          <h2 className="font-zilla text-xl font-semibold mb-1">Guardian information</h2>
          <p className="text-[12px] text-ink/50 mb-2">Kept on your record as history.</p>
          {member.guardians.map((g, i) => (
            <div key={i} className="text-[14px] border-b border-ink/8 py-2 last:border-0">
              <span className="font-semibold">{g.name}</span>{g.linked ? ` · linked ${g.member_number}` : ""} · {g.phone}{g.email ? ` · ${g.email}` : ""}
            </div>
          ))}
        </div>
      )}

      {(member.linked_youth || []).length > 0 && (
        <div className="bg-cream border border-ink/15 rounded-xl p-5" data-testid="my-linked-youth">
          <h2 className="font-zilla text-xl font-semibold mb-1">Youth linked to you</h2>
          {member.linked_youth.map((y) => (
            <div key={y.member_id} className="text-[14px] border-b border-ink/8 py-2 last:border-0">
              <span className="font-semibold">{y.name}</span> · {y.member_number} · <span className="capitalize">{y.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
